import { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Wallet, TrendingUp, TrendingDown, AlertCircle, Clock, ArrowRight } from 'lucide-react';
import { Card } from '@components/ui';
import clsx from 'clsx';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler);

/**
 * NakitAkisi - Cash Flow Projeksiyonu
 * Müteahhitin "Bu ay kasadan ne kadar çıkacak?" sorusuna cevap veren bileşen.
 */
export default function NakitAkisi({ summary, formatCurrency, formatCompactCurrency, formatDate }) {
    // Kümülatif nakit akışı hesapla
    const cashFlowData = useMemo(() => {
        if (!summary?.monthlyBreakdown) return { labels: [], cumulative: [], net: [] };

        let cumulative = 0;
        const labels = [];
        const cumulativeArr = [];
        const netArr = [];

        summary.monthlyBreakdown.forEach(m => {
            const net = (m.income || 0) - (m.expense || 0);
            cumulative += net;
            labels.push(m.monthLabel || m.month);
            cumulativeArr.push(cumulative);
            netArr.push(net);
        });

        return { labels, cumulative: cumulativeArr, net: netArr };
    }, [summary]);

    // Bu ay ve önceki ay karşılaştırması
    const currentMonthSummary = useMemo(() => {
        if (!summary?.monthlyBreakdown || summary.monthlyBreakdown.length === 0) return null;
        const last = summary.monthlyBreakdown[summary.monthlyBreakdown.length - 1];
        const prev = summary.monthlyBreakdown.length > 1 ? summary.monthlyBreakdown[summary.monthlyBreakdown.length - 2] : null;
        return { current: last, previous: prev };
    }, [summary]);

    // Chart data
    const chartDataConfig = {
        labels: cashFlowData.labels,
        datasets: [
            {
                label: 'Kümülatif Nakit Akışı',
                data: cashFlowData.cumulative,
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: (ctx) => {
                    if (!ctx.chart?.chartArea) return 'rgba(59, 130, 246, 0.1)';
                    const gradient = ctx.chart.ctx.createLinearGradient(0, ctx.chart.chartArea.top, 0, ctx.chart.chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(59, 130, 246, 0.3)');
                    gradient.addColorStop(1, 'rgba(59, 130, 246, 0.02)');
                    return gradient;
                },
                borderWidth: 3,
                tension: 0.4,
                fill: true,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: cashFlowData.cumulative.map(v => v >= 0 ? 'rgb(16, 185, 129)' : 'rgb(244, 63, 94)'),
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
            },
            {
                label: 'Aylık Net',
                data: cashFlowData.net,
                borderColor: 'rgba(156, 163, 175, 0.5)',
                borderWidth: 1.5,
                borderDash: [5, 5],
                tension: 0.4,
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: { size: 12, weight: '500' },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                titleFont: { size: 13, weight: 'bold' },
                bodyFont: { size: 12 },
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        const formatted = formatCurrency ? formatCurrency(value) : `₺${value.toLocaleString('tr-TR')}`;
                        return `${context.dataset.label}: ${formatted}`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { size: 11 } },
            },
            y: {
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                ticks: {
                    font: { size: 11 },
                    callback: (value) => {
                        if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
                        if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(0)}K`;
                        return value;
                    },
                },
            },
        },
    };

    // Toplam gelir/gider ve kasa bilgisi
    const totalIncome = summary?.income?.total || 0;
    const totalExpense = summary?.expense?.total || 0;
    const netCash = totalIncome - totalExpense;
    const pendingReceivables = summary?.collections?.pending || 0;
    const pendingPayables = summary?.payments?.pending || 0;
    const overdueReceivables = summary?.collections?.overdue || 0;

    return (
        <div className="space-y-6">
            {/* Ana Kasa Durumu - 4 KPI */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-bg-secondary rounded-xl border border-border-light p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center">
                            <TrendingUp size={16} className="text-success" />
                        </div>
                        <span className="text-xs text-text-muted">Beklenen Tahsilat</span>
                    </div>
                    <p className="text-xl font-bold text-success">{formatCompactCurrency(pendingReceivables)}</p>
                    <p className="text-xs text-text-tertiary mt-1">Tahsil edilmeyi bekliyor</p>
                </div>
                <div className="bg-bg-secondary rounded-xl border border-border-light p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-error/10 flex items-center justify-center">
                            <TrendingDown size={16} className="text-error" />
                        </div>
                        <span className="text-xs text-text-muted">Ödenecek Borç</span>
                    </div>
                    <p className="text-xl font-bold text-error">{formatCompactCurrency(pendingPayables)}</p>
                    <p className="text-xs text-text-tertiary mt-1">Ödenmesi gereken</p>
                </div>
                <div className="bg-bg-secondary rounded-xl border border-border-light p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${netCash >= 0 ? 'bg-primary/10' : 'bg-error/10'}`}>
                            <Wallet size={16} className={netCash >= 0 ? 'text-primary' : 'text-error'} />
                        </div>
                        <span className="text-xs text-text-muted">Net Kasa</span>
                    </div>
                    <p className={clsx("text-xl font-bold", netCash >= 0 ? 'text-primary' : 'text-error')}>{formatCompactCurrency(netCash)}</p>
                    <p className="text-xs text-text-tertiary mt-1">Gelir − Gider farkı</p>
                </div>
                <div className="bg-bg-secondary rounded-xl border border-border-light p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
                            <AlertCircle size={16} className="text-warning" />
                        </div>
                        <span className="text-xs text-text-muted">Gecikmiş Alacak</span>
                    </div>
                    <p className="text-xl font-bold text-warning">{formatCompactCurrency(overdueReceivables)}</p>
                    <p className="text-xs text-text-tertiary mt-1">Vadesi geçmiş</p>
                </div>
            </div>

            {/* Kümülatif Nakit Akışı Grafiği */}
            <Card padding="lg">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base sm:text-lg font-semibold text-text-primary flex items-center gap-2">
                        <Wallet size={20} className="text-primary" />
                        Nakit Akışı Projeksiyonu
                    </h3>
                    <span className="text-xs text-text-muted">Kümülatif bakiye (aylık)</span>
                </div>
                <div className="h-64 sm:h-72 md:h-80">
                    <Line data={chartDataConfig} options={chartOptions} />
                </div>
            </Card>

            {/* Bu Ay vs Önceki Ay Karşılaştırması */}
            {currentMonthSummary && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Bu Ay */}
                    <Card padding="lg">
                        <h3 className="text-base font-semibold text-text-primary mb-4 flex items-center gap-2">
                            <Clock size={18} className="text-text-muted" />
                            Son Dönem Özeti — {currentMonthSummary.current.monthLabel}
                        </h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-success/5 rounded-lg">
                                <span className="text-sm text-text-secondary">Gelen (Gelir)</span>
                                <span className="font-bold text-success">{formatCurrency(currentMonthSummary.current.income || 0)}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-error/5 rounded-lg">
                                <span className="text-sm text-text-secondary">Çıkan (Gider)</span>
                                <span className="font-bold text-error">{formatCurrency(currentMonthSummary.current.expense || 0)}</span>
                            </div>
                            <div className={clsx("flex items-center justify-between p-3 rounded-lg border-2 border-dashed",
                                (currentMonthSummary.current.income - currentMonthSummary.current.expense) >= 0 ? 'border-success/30' : 'border-error/30'
                            )}>
                                <span className="text-sm font-medium text-text-primary">Net Fark</span>
                                <span className={clsx("text-lg font-bold",
                                    (currentMonthSummary.current.income - currentMonthSummary.current.expense) >= 0 ? 'text-success' : 'text-error'
                                )}>
                                    {formatCurrency((currentMonthSummary.current.income || 0) - (currentMonthSummary.current.expense || 0))}
                                </span>
                            </div>
                        </div>
                    </Card>

                    {/* Önceki Ay (varsa) */}
                    {currentMonthSummary.previous ? (
                        <Card padding="lg">
                            <h3 className="text-base font-semibold text-text-primary mb-4 flex items-center gap-2">
                                <ArrowRight size={18} className="text-text-muted" />
                                Önceki Dönem — {currentMonthSummary.previous.monthLabel}
                            </h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                                    <span className="text-sm text-text-secondary">Gelen (Gelir)</span>
                                    <span className="font-semibold text-text-primary">{formatCurrency(currentMonthSummary.previous.income || 0)}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                                    <span className="text-sm text-text-secondary">Çıkan (Gider)</span>
                                    <span className="font-semibold text-text-primary">{formatCurrency(currentMonthSummary.previous.expense || 0)}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                                    <span className="text-sm text-text-secondary">Net Fark</span>
                                    <span className={clsx("font-bold",
                                        (currentMonthSummary.previous.income - currentMonthSummary.previous.expense) >= 0 ? 'text-success' : 'text-error'
                                    )}>
                                        {formatCurrency((currentMonthSummary.previous.income || 0) - (currentMonthSummary.previous.expense || 0))}
                                    </span>
                                </div>
                            </div>
                        </Card>
                    ) : (
                        <Card padding="lg">
                            <div className="h-full flex items-center justify-center text-text-muted">
                                <p className="text-sm">Önceki dönem verisi bulunmuyor.</p>
                            </div>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}
